/**
 * @file OpsOverview.tsx
 * @description View 1 — The unified operational truth.
 *
 * Layout:
 *   - KPI Cards row (Bed Availability, Lab TAT, Conflict Count, Occupancy %)
 *   - Bed Map (visual all-bed grid)
 *   - Lab TAT Chart + Bottleneck Panel (side by side)
 */

import { BedDouble, FlaskConical, AlertTriangle, Activity, Volume2 } from "lucide-react";
import { KPICard } from "../Dashboard/KPICard";
import { BedMap } from "../Dashboard/BedMap";
import { LabTATChart } from "../Dashboard/LabTATChart";
import { BottleneckPanel } from "../Dashboard/BottleneckPanel";
import { UnifiedRecord, SystemMetrics } from "../../types";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSpeech } from "../../hooks/useSpeech";

interface OpsOverviewProps {
  records: UnifiedRecord[];
  metrics: SystemMetrics | null;
  loading: boolean;
}

export function OpsOverview({ records, metrics, loading }: OpsOverviewProps) {
  const { t } = useLanguage();
  const { speak, isSpeaking, supported } = useSpeech();
  // Calculate dynamic Lab TAT trend from records
  const computeChartData = () => {
    const labs = records.flatMap(r => r.lab_results);
    if (labs.length === 0) return undefined; // Will fallback to mock data

    // Group by hour
    const hourly: Record<string, number[]> = {};
    labs.forEach(lab => {
      if (!lab.result_datetime) return;
      // Extract hour, e.g., "09:00" from "2024-08-01T09:15:00Z"
      const date = new Date(lab.result_datetime);
      if (isNaN(date.getTime())) return;
      
      const hourStr = date.getHours().toString().padStart(2, "0") + ":00";
      if (!hourly[hourStr]) hourly[hourStr] = [];
      hourly[hourStr].push(lab.tat_minutes);
    });

    // If we couldn't parse any, fallback
    const hours = Object.keys(hourly).sort();
    if (hours.length === 0) return undefined;

    return hours.map(time => {
      const tats = hourly[time].sort((a, b) => a - b);
      const avg_tat = Math.round(tats.reduce((sum, val) => sum + val, 0) / tats.length);
      const p90Idx = Math.floor(tats.length * 0.9);
      const p90_tat = Math.round(tats[p90Idx]);
      return { time, avg_tat, p90_tat };
    });
  };

  const chartData = computeChartData();

  return (
    <div className="space-y-6 animate-fade-in">

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative">
        <KPICard
          id="kpi-bed-availability"
          label={t("kpi_available_beds")}
          value={loading ? "–" : (metrics?.available_beds ?? 0)}
          unit={`/ ${metrics?.total_beds ?? 0}`}
          subtext={`${metrics?.occupancy_rate_pct ?? 0}% occupied`}
          icon={BedDouble}
          variant="teal"
          trend="neutral"
          trendLabel="Real-time"
          loading={loading}
        />
        <KPICard
          id="kpi-occupancy"
          label={t("kpi_occupancy")}
          value={loading ? "–" : `${metrics?.occupancy_rate_pct ?? 0}`}
          unit="%"
          subtext={`${metrics?.occupied_beds ?? 0} of ${metrics?.total_beds ?? 0} beds in use`}
          icon={Activity}
          variant="blue"
          loading={loading}
        />
        <KPICard
          id="kpi-avg-tat"
          label={t("kpi_avg_tat")}
          value={loading ? "–" : (metrics?.avg_lab_tat_minutes ?? 0)}
          unit="min"
          subtext={`P90: ${metrics?.p90_lab_tat_minutes ?? 0} min · ${metrics?.stat_orders ?? 0} STAT orders`}
          icon={FlaskConical}
          variant={
            (metrics?.avg_lab_tat_minutes ?? 0) > 120 ? "red" : "amber"
          }
          trend={
            (metrics?.avg_lab_tat_minutes ?? 0) > 120 ? "down" : "up"
          }
          trendLabel={
            (metrics?.avg_lab_tat_minutes ?? 0) > 120
              ? "Above 2-hour target"
              : "Within target"
          }
          loading={loading}
        />
        <KPICard
          id="kpi-conflicts"
          label={t("kpi_active_conflicts")}
          value={loading ? "–" : (metrics?.total_conflicts ?? 0)}
          subtext={`${metrics?.pending_clean_beds ?? 0} beds pending clean`}
          icon={AlertTriangle}
          variant={(metrics?.total_conflicts ?? 0) > 0 ? "red" : "teal"}
          trend={(metrics?.total_conflicts ?? 0) > 0 ? "down" : "neutral"}
          trendLabel={
            (metrics?.total_conflicts ?? 0) > 0
              ? "Requires attention"
              : "All clear"
          }
          loading={loading}
        />
        
        {supported && (
          <div className="absolute -top-12 right-0 hidden sm:flex items-center gap-2">
            <button
              onClick={() => speak(`Occupied beds are ${metrics?.occupied_beds ?? 0}. Available beds are ${metrics?.available_beds ?? 0}. Average lab turnaround time is ${metrics?.avg_lab_tat_minutes ?? 0} minutes.`)}
              className={`p-2 rounded-lg border transition-colors ${
                isSpeaking ? "bg-teal-500/20 border-teal-500/30 text-teal-400 animate-pulse-slow" : "bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white"
              }`}
              title={t("btn_speak")}
            >
              <Volume2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* ── Bed Map ── */}
      <BedMap records={records} />

      {/* ── Charts Row ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <LabTATChart data={chartData} />
        <BottleneckPanel records={records} />
      </div>

      {/* ── Quick Patient Table ── */}
      <PatientSummaryTable records={records} loading={loading} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Patient Summary Table
// ─────────────────────────────────────────────────────────────────────────────

function PatientSummaryTable({ records, loading }: { records: UnifiedRecord[]; loading: boolean }) {
  const patientRecords = records
    .filter((r) => r.patient_name && !r.patient_id.startsWith("BED-ONLY-"))
    .sort((a, b) => Number(b.has_conflict) - Number(a.has_conflict));

  return (
    <div
      className="rounded-2xl border border-white/8 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
      }}
    >
      <div className="px-6 py-4 border-b border-white/5">
        <h2 className="text-white font-semibold text-base">Patient Summary</h2>
        <p className="text-gray-500 text-xs mt-0.5">
          {patientRecords.length} active patient records · conflicts flagged in yellow
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5">
              {["Patient", "ID", "Bed", "Ward", "HIS Status", "Avg Lab TAT", "Flags"].map(
                (h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <td key={j} className="px-6 py-3">
                      <div className="h-4 rounded bg-white/5 animate-pulse w-24" />
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              patientRecords.map((r) => (
                <tr
                  key={r.patient_id}
                  className={`transition-colors duration-150 hover:bg-white/3 ${
                    r.has_conflict ? "bg-yellow-500/3" : ""
                  }`}
                >
                  <td className="px-6 py-3 text-white font-medium">
                    {r.patient_name || "—"}
                  </td>
                  <td className="px-6 py-3 font-mono text-gray-400 text-xs">
                    {r.is_orphaned_lab_record ? (
                      <span className="text-purple-400">{r.patient_id}</span>
                    ) : (
                      r.patient_id
                    )}
                  </td>
                  <td className="px-6 py-3 font-mono text-gray-300">
                    {r.resolved_bed || "—"}
                  </td>
                  <td className="px-6 py-3 text-gray-400">{r.ward || "—"}</td>
                  <td className="px-6 py-3">
                    <HisStatusBadge status={r.his_clinical_status} />
                  </td>
                  <td className="px-6 py-3 text-gray-300 font-mono">
                    {r.avg_lab_tat_minutes > 0
                      ? `${r.avg_lab_tat_minutes} min`
                      : "—"}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex flex-wrap gap-1">
                      {r.conflict_flags
                        .filter((f) => f !== "Clean")
                        .map((f) => (
                          <ConflictBadge key={f} type={f} />
                        ))}
                      {!r.has_conflict && (
                        <span className="text-green-400 text-xs">✓ Clean</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HisStatusBadge({ status }: { status: string }) {
  const cfg: Record<string, string> = {
    Admitted: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    Discharged: "bg-gray-500/15 text-gray-400 border-gray-500/30",
    Transferred: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    Pending: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${
        cfg[status] ?? "bg-white/5 text-gray-500 border-white/10"
      }`}
    >
      {status || "—"}
    </span>
  );
}

function ConflictBadge({ type }: { type: string }) {
  const cfg: Record<string, string> = {
    "Status Mismatch": "bg-red-500/15 text-red-400 border-red-500/30",
    "Location Desync": "bg-amber-500/15 text-amber-400 border-amber-500/30",
    "Orphaned Lab Record": "bg-purple-500/15 text-purple-400 border-purple-500/30",
    "Post-Discharge Anomaly": "bg-orange-500/15 text-orange-400 border-orange-500/30",
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${
        cfg[type] ?? "bg-white/5 text-gray-500 border-white/10"
      }`}
    >
      {type}
    </span>
  );
}
